import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, Check, X, ShoppingBag, Search } from "lucide-react";
import { checkDomain } from "../../lib/api";

const EASE = [0.16, 1, 0.3, 1];
const TLDS = [".sa", ".com.sa", ".org.sa", ".net.sa", ".gov.sa", ".edu.sa"];

/**
 * HeroC — Editorial Tech
 * Magazine grid: massive .sa numeral as page anchor, byline metadata,
 * column rules, asymmetric layout, oversized prices as design.
 */
export const HeroC = ({ onAddToCart }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [activeTLD, setActiveTLD] = useState(".sa");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const submit = async (e) => {
        e?.preventDefault?.();
        const q = query.trim();
        if (!q) return;
        setLoading(true); setError(""); setResult(null);
        try {
            const data = await checkDomain(q);
            setResult(data);
        } catch (err) {
            setError(
                err?.response?.data?.detail === "invalid_domain_label"
                    ? "Try a clean label, e.g. mybrand"
                    : "Could not check availability — try again.",
            );
        } finally { setLoading(false); }
    };

    return (
        <section
            id="hero-search"
            className="relative isolate overflow-hidden bg-limestone-100 pt-[110px] lg:pt-[130px]"
            data-testid="hero-c"
        >
            {/* Top masthead-style rule */}
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <div className="border-b-2 border-ink-800 pb-4 flex flex-wrap items-baseline justify-between gap-4 font-mono text-[12px] uppercase tracking-[0.18em] text-ink-700">
                    <span className="font-bold">№ 012 · NourNet Field Notes</span>
                    <span className="text-ink-500">Riyadh · Feb 2026</span>
                    <span className="text-ink-500">Issue: <span className="text-madder-500 font-bold">Saudi sovereignty online</span></span>
                </div>
            </div>

            <div className="relative mx-auto max-w-[1320px] px-5 pb-16 lg:px-8 lg:pb-24">
                <div className="grid grid-cols-12 gap-x-6 lg:gap-x-10 mt-12">
                    {/* Massive .sa numeral as design anchor — left column */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="col-span-12 lg:col-span-5 relative"
                    >
                        <div className="sticky top-32">
                            <p className="font-mono text-[12px] uppercase tracking-[0.2em] font-bold text-ink-600 border-t-2 border-ink-800 pt-4 mb-2">
                                Cover · the .sa registry
                            </p>
                            <p className="font-display font-extrabold leading-[0.78] tracking-[-0.06em] text-ink-800 num-ltr"
                                style={{ fontSize: "clamp(180px, 26vw, 360px)" }}
                                data-testid="hero-c-numeral"
                            >
                                .sa
                            </p>
                            <p className="mt-2 font-display text-[24px] sm:text-[28px] font-bold leading-tight tracking-tight text-madder-500">
                                a registry, refined
                            </p>
                            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-ink-600">Yr.</p>
                                    <p className="font-display text-[28px] font-bold tabular-nums num-ltr text-ink-800">2014</p>
                                </div>
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-ink-600">Domains</p>
                                    <p className="font-display text-[28px] font-bold tabular-nums num-ltr text-redsea-500">240K+</p>
                                </div>
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] font-bold text-ink-600">Up.</p>
                                    <p className="font-display text-[28px] font-bold tabular-nums num-ltr text-souq-500">99.99</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* RIGHT column — editorial content (7/12) */}
                    <div className="col-span-12 lg:col-span-7 mt-12 lg:mt-0">
                        {/* Byline */}
                        <motion.div
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="font-mono text-[11px] uppercase tracking-[0.22em] font-bold text-madder-500 mb-3"
                            data-testid="hero-c-byline"
                        >
                            Filed from Riyadh — by NourNet engineering · 4 min read
                        </motion.div>

                        {/* Headline — editorial style with mixed weights */}
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
                            className="display-headline max-w-[18ch]"
                            style={{ fontSize: "clamp(48px, 6vw, 88px)", lineHeight: 0.98 }}
                            data-testid="hero-c-title"
                        >
                            {t("home.heroQuestion1")}{" "}
                            <span className="text-madder-500">{t("home.heroQuestionAccent")}</span>{" "}
                            {t("home.heroQuestion2")}
                        </motion.h1>

                        {/* Editorial drop-cap lede */}
                        <motion.p
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.12 }}
                            className="mt-7 max-w-[58ch] text-[20px] leading-[1.55] font-medium text-ink-700 first-letter:font-display first-letter:font-extrabold first-letter:text-[64px] first-letter:leading-[0.85] first-letter:float-start first-letter:me-2 first-letter:mt-1 first-letter:text-madder-500"
                        >
                            {t("home.heroSubtitle")}
                        </motion.p>

                        {/* Search — minimal, integrated */}
                        <motion.form
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.2 }}
                            onSubmit={submit}
                            className="mt-10"
                            data-testid="hero-c-search-form"
                        >
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-ink-600 border-t-2 border-ink-800 pt-3 mb-4">
                                Search · the registry
                            </p>
                            <div className="flex items-center gap-2 border-b-4 border-ink-800 pb-2">
                                <Search className="h-6 w-6 text-ink-700 ms-1" strokeWidth={2.4} />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("home.heroSearchPlaceholder")}
                                    aria-label={t("home.heroSearchPlaceholder")}
                                    className="h-[60px] flex-1 bg-transparent px-2 text-[24px] font-bold text-ink-800 placeholder:text-ink-400 placeholder:font-medium focus:outline-none"
                                    data-testid="hero-c-search-input"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || !query.trim()}
                                    data-testid="hero-c-search-submit"
                                    className="inline-flex h-[56px] min-w-[140px] items-center justify-center gap-2 rounded-[8px] bg-ink-800 px-6 text-[16px] font-bold text-white transition-all duration-200 hover:bg-madder-500 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {loading ? (
                                        <><Loader2 className="h-5 w-5 animate-spin" />{t("cta.checking")}</>
                                    ) : (
                                        <>{t("cta.search")}<ArrowRight className="h-5 w-5 rtl-mirror" /></>
                                    )}
                                </button>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-2">
                                {TLDS.map((tld) => {
                                    const active = activeTLD === tld;
                                    return (
                                        <button
                                            type="button"
                                            key={tld}
                                            onClick={() => {
                                                setActiveTLD(tld);
                                                const base = (query.trim() || "").replace(/\.(sa|com\.sa|org\.sa|net\.sa|gov\.sa|edu\.sa|com|net)$/i, "");
                                                setQuery((base || "mybrand") + tld);
                                                setTimeout(() => inputRef.current?.focus(), 0);
                                            }}
                                            data-testid={`hero-c-tld-${tld.replace(/\./g, "")}`}
                                            className={`inline-flex h-12 min-h-[48px] items-center rounded-[6px] border-2 px-4 font-mono text-[15px] font-semibold transition-all duration-200 ease-nour num-ltr ${
                                                active
                                                    ? "border-madder-500 bg-madder-500 text-white"
                                                    : "border-ink-300 bg-transparent text-ink-800 hover:border-ink-800"
                                            }`}
                                        >
                                            {tld}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-5 flex items-baseline justify-between gap-4 border-b border-ink-300 pb-3 font-mono text-[12px] uppercase tracking-[0.16em] font-bold text-ink-600">
                                <span>From <span className="text-madder-500 num-ltr">180 SAR/yr</span></span>
                                <span>No card needed</span>
                                <span className="text-ink-700 num-ltr">240,000+ trusted</span>
                            </div>
                        </motion.form>

                        {/* Result panel */}
                        <AnimatePresence mode="wait">
                            {(loading || result || error) && (
                                <motion.div
                                    key={loading ? "load" : result ? "ok" : "err"}
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.24, ease: EASE }}
                                    className="mt-6"
                                    data-testid="hero-c-result"
                                >
                                    {loading && (
                                        <div className="border-2 border-ink-300 p-5">
                                            <div className="h-4 w-44 animate-pulse rounded-full bg-ink-200" />
                                        </div>
                                    )}
                                    {error && !loading && (
                                        <div className="border-2 border-madder-500 bg-madder-50 p-5 text-[16px] font-semibold text-madder-700">{error}</div>
                                    )}
                                    {result && !loading && (
                                        <div className="border-y-4 border-ink-800 py-6">
                                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                                <div className="flex items-center gap-4">
                                                    <span className={`inline-flex h-14 w-14 items-center justify-center rounded-[6px] ${result.primary.available ? "bg-redsea-500 text-white" : "bg-ink-100 text-ink-500"}`}>
                                                        {result.primary.available ? <Check className="h-7 w-7" strokeWidth={3} /> : <X className="h-7 w-7" strokeWidth={3} />}
                                                    </span>
                                                    <div>
                                                        <p className="font-mono text-[20px] font-bold text-ink-800 num-ltr">{result.primary.domain}</p>
                                                        <p className="text-[16px] font-bold mt-1">
                                                            {result.primary.available ? <span className="text-redsea-500 uppercase tracking-wider text-[14px]">{t("domainResult.available")}</span> : <span className="text-ink-500">{t("domainResult.taken")}</span>}
                                                            {result.primary.available && (
                                                                <> <span className="text-ink-400">·</span> <span className="num-ltr tabular-nums text-ink-800">{result.primary.price_sar} {t("domainResult.perYear")}</span></>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                {result.primary.available && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onAddToCart?.(result.primary)}
                                                        data-testid="hero-c-add-to-cart"
                                                        className="inline-flex h-[56px] items-center gap-2 rounded-btn bg-madder-500 px-6 text-[16px] font-bold text-white transition-all duration-200 hover:bg-madder-600"
                                                    >
                                                        <ShoppingBag className="h-5 w-5" />
                                                        {t("domainResult.addToCart")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};
